import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { EnvelopeSimple, Lock, SignIn, CircleNotch } from "@phosphor-icons/react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const tenant = useTenant();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Identifiants incorrects. Veuillez réessayer.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8">
          <img
            src={tenant.logo}
            alt={tenant.name}
            className="w-20 h-20 mx-auto mb-4 object-contain"
          />
          <h1 className="font-heading text-2xl font-bold text-[color:var(--text-primary)]">
            Stock Cabinet
          </h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">
            {tenant.name}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-[color:var(--border-soft)] p-8">
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)] mb-6">
            Connexion
          </h2>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[color:var(--text-secondary)] mb-1.5"
              >
                Email
              </label>
              <div className="relative">
                <EnvelopeSimple
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
                />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[color:var(--border-soft)] bg-[color:var(--bg)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)] focus:border-transparent transition"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[color:var(--text-secondary)] mb-1.5"
              >
                Mot de passe
              </label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
                />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-[color:var(--border-soft)] bg-[color:var(--bg)] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)] focus:border-transparent transition"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[color:var(--brand)] text-white font-semibold hover:opacity-90 disabled:opacity-50 transition press-scale"
            >
              {loading ? (
                <CircleNotch size={20} className="animate-spin" />
              ) : (
                <SignIn size={20} weight="bold" />
              )}
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-[color:var(--brand)] hover:underline"
            >
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="mt-3 text-center border-t border-[color:var(--border-soft)] pt-4">
            <p className="text-sm text-[color:var(--text-muted)]">
              Pas encore de compte ?{" "}
              <Link
                to="/register"
                className="font-medium text-[color:var(--brand)] hover:underline"
              >
                Créer mon espace gratuitement
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
